import styled from 'styled-components';

import Menu from 'components/shared/menu';
import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import OverflowIcon from 'svg/OverflowIcon';
import PencilIcon from 'svg/PencilIcon';
import PencilIconOutline from 'svg/PencilIconOutline';
import PlusIcon from 'svg/PlusIcon';
import PlusIconOutline from 'svg/PlusIconOutline';

import { VALUE_NAMES } from '../constants';

import { LinkButton } from './BenefitsTable';
import { BenefitsView } from './newFormatBenefits';

// The Payer Name / Member ID row shown fixed below the coverage tabs (OLIO-3116, replacing the
// details card that used to open every slideout state). Reads the check inputs off the workflow
// values and offers the Edit entry point.

interface Props {
  workflow: Workflow;
  // Undefined when the user may not edit the referral, which hides the link (OLIO-2615).
  onEditDetails?: () => void;
  // The coverage whose details these are; null on a referral with no coverages.
  facetId: string | null;
  // A coverage tab nobody has filled yet (the empty Secondary). Its payer and member ID are blank,
  // never the run-level `plan_type` a null facetId would otherwise fall back to.
  emptySlot?: boolean;
  // The settled result's active/inactive call (OLIO-3116) — undefined for every other slideout
  // state (running, pending, select-payer, error, …), which renders no pill at all. Replaces the
  // green/red banner that used to sit atop the result set.
  coverageStatus?: 'active' | 'inactive';
  // Undefined when benefits can't be documented by hand right now (no grant, or a check is running).
  onAddManually?: () => void;
  // Set when the plain-language view shows: the row then reads what the payer returned and adds the group
  // number. The badge still follows the check's status, the same one that picks the screen.
  benefitsHeader?: BenefitsView['header'];
  // `card` is the phone's version: the first card in the scrolling body rather than a fixed row, with
  // each label above its value and the two links in an overflow menu. One line of label-and-value
  // pairs plus two links is wider than a phone, and run together it read as one
  // string (Figma 15133:26383, 15133:26462).
  layout?: 'bar' | 'card';
}

export default function EligibilityDetailsHeader({
  workflow,
  onEditDetails,
  facetId,
  emptySlot = false,
  coverageStatus,
  onAddManually,
  benefitsHeader,
  layout = 'bar',
}: Props) {
  // The COVERAGE's payer, never a referral-wide one: reading a run-level payer here would put the
  // primary's payer next to the secondary's member ID, the mismatch the no-fallback rule exists to
  // prevent. Only a referral with no coverages at all (facetId null) falls back to `plan_type`,
  // because that referral predates per-coverage verification and its own is run-level. Referral
  // intake stopped capturing that value in OLIO-2890, so nothing new writes it.
  const payerName = emptySlot
    ? undefined
    : facetId
      ? workflow.valueFor(VALUE_NAMES.PAYER_NAME, { facetId })?.value
      : workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  const memberId = emptySlot ? undefined : workflow.valueFor(VALUE_NAMES.MEMBER_ID, { facetId })?.value;
  // Referral-level like name (patient DOB doesn't vary by coverage) — kept in the bar so the
  // "No Results Found" copy's "verify member ID, name, and date of birth" has a DOB on screen to
  // check against (name lives in the slideout title).
  const dob = simpleDate(workflow.valueFor(VALUE_NAMES.PATIENT_DOB)?.value);

  // The plain-language results view reads what the payer returned and adds the group number; every
  // other state reads the check's own inputs. Its pill also says "Inactive" where the others say "No
  // Active Coverage".
  const pill = coverageStatus && (
    <CoveragePill $tone={coverageStatus} data-testid='coverage-pill'>
      {coverageStatus === 'active' ? 'Active Coverage' : benefitsHeader ? 'Inactive' : 'No Active Coverage'}
    </CoveragePill>
  );

  if (layout === 'card') {
    return (
      <Card data-testid='eligibility-details-header'>
        <CardField $clearsMenu>
          <CardLabel>Payer Name</CardLabel>
          <CardValue>{benefitsHeader ? benefitsHeader.payer : payerName || '—'}</CardValue>
        </CardField>
        <CardRow>
          <CardField>
            <CardLabel>Member ID</CardLabel>
            <CardValue>{benefitsHeader ? benefitsHeader.memberId : memberId || '—'}</CardValue>
          </CardField>
          {benefitsHeader && (
            <CardField>
              <CardLabel>Group Number</CardLabel>
              <CardValue>{benefitsHeader.groupNumber}</CardValue>
            </CardField>
          )}
          <CardField>
            <CardLabel>DOB</CardLabel>
            <CardValue>{benefitsHeader ? benefitsHeader.dob : dob || '—'}</CardValue>
          </CardField>
        </CardRow>
        {pill && <div>{pill}</div>}
        {(onEditDetails || onAddManually) && (
          <Menu>
            <Menu.Trigger>
              <OverflowTrigger type='button' aria-label='Coverage options' data-testid='eligibility-details-menu'>
                <OverflowIcon />
              </OverflowTrigger>
            </Menu.Trigger>
            <Menu.Content position='right'>
              {onEditDetails && (
                <Menu.Item onClick={onEditDetails} data-testid='edit-details'>
                  <Menu.Icon>
                    <PencilIconOutline width={14} height={14} />
                  </Menu.Icon>
                  Edit patient details
                </Menu.Item>
              )}
              {onAddManually && (
                <Menu.Item onClick={onAddManually} data-testid='add-benefits-manually'>
                  <Menu.Icon>
                    <PlusIconOutline width={14} height={14} />
                  </Menu.Icon>
                  Add benefits manually
                </Menu.Item>
              )}
            </Menu.Content>
          </Menu>
        )}
      </Card>
    );
  }

  if (benefitsHeader) {
    return (
      <Row data-testid='eligibility-details-header'>
        <LeftGroup>
          <Details>
            Payer Name: <Value>{benefitsHeader.payer}</Value>
            &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; Member ID: <Value>{benefitsHeader.memberId}</Value>
            &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; Group Number: <Value>{benefitsHeader.groupNumber}</Value>
            &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; DOB: <Value>{benefitsHeader.dob}</Value>
          </Details>
          {pill}
        </LeftGroup>
        <HeaderLinks onAddManually={onAddManually} onEditDetails={onEditDetails} />
      </Row>
    );
  }

  return (
    <Row data-testid='eligibility-details-header'>
      <LeftGroup>
        <Details>
          Payer Name: <Value>{payerName || '—'}</Value>
          &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; Member ID: <Value>{memberId || '—'}</Value>
          &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; DOB: <Value>{dob || '—'}</Value>
        </Details>
        {pill}
      </LeftGroup>
      <HeaderLinks onAddManually={onAddManually} onEditDetails={onEditDetails} />
    </Row>
  );
}

function HeaderLinks({ onAddManually, onEditDetails }: Pick<Props, 'onAddManually' | 'onEditDetails'>) {
  return (
    <Links>
      {onAddManually && (
        <LinkButton type='button' onClick={onAddManually} data-testid='add-benefits-manually'>
          <PlusIcon color={colors.blue100} width={10} height={10} />
          Add Benefits Manually
        </LinkButton>
      )}
      {onAddManually && onEditDetails && <Divider />}
      {onEditDetails && (
        <LinkButton type='button' onClick={onEditDetails} data-testid='edit-details'>
          <PencilIcon color={colors.blue100} width={12} height={12} />
          Edit
        </LinkButton>
      )}
    </Links>
  );
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const Details = styled(BodySmall)`
  color: ${colors.black50};
`;

const CoveragePill = styled.span<{ $tone: 'active' | 'inactive' }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 48px;
  font-size: 10px;
  font-weight: 500;
  color: ${colors.white};
  background: ${({ $tone }) => ($tone === 'active' ? colors.green100 : colors.red100)};
`;

const Value = styled.span`
  color: ${colors.black100};
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const Divider = styled.span`
  align-self: stretch;
  width: 1px;
  background: ${colors.black10};
`;

// Rendered only on a phone (the slideout branches on useIsMobile), so none of this sits behind a
// media query: a rule gated on `devices.mobile` would be off in the sliver where the hook already
// says phone.
const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
`;

const CardRow = styled.div`
  display: flex;
  gap: 24px;
`;

// Payer Name shares its line with the overflow trigger, so it stops short of it rather than running
// a long payer underneath the icon.
const CardField = styled.div<{ $clearsMenu?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  ${({ $clearsMenu }) => $clearsMenu && 'padding-right: 28px;'}
`;

const CardLabel = styled.span`
  font-size: 10px;
  line-height: 10px;
  font-weight: 700;
  color: ${colors.brandNavy100};
`;

const CardValue = styled.span`
  font-size: 12px;
  line-height: 15px;
  color: ${colors.black100};
  overflow-wrap: anywhere;
`;

// A 44px box around a 12×3 glyph: the touch minimum, unconditionally, since the card only exists on
// a phone. Placed so the glyph lands where the mock draws it — level with the Payer Name label and
// 16px in from the card's right edge — while the box runs out to that edge.
const OverflowTrigger = styled.button`
  position: absolute;
  top: 1.5px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: none;
  color: ${colors.black75};
  cursor: pointer;
`;
